import type { PhaseStatus, SpecialistState } from "../types/api";
import { humanizeSpecialty } from "../utils/format";

interface SpecialistPanelsProps {
  specialists: Record<string, SpecialistState>;
  phase3Status: PhaseStatus;
  pendingDomains?: string[];
  activeTool?: string | null;
}

function specialistKeyFromTool(name: string): string | null {
  if (name.endsWith("_specialist")) return name.slice(0, -"_specialist".length);
  return null;
}

function PanelSkeleton() {
  return (
    <div className="spec-panel skeleton-panel">
      <div className="sk-line w92" />
      <div className="sk-line w78" />
      <div className="sk-line w60" />
    </div>
  );
}

export function SpecialistPanels({
  specialists,
  phase3Status,
  pendingDomains = [],
  activeTool,
}: SpecialistPanelsProps) {
  const activeSpecKey = activeTool ? specialistKeyFromTool(activeTool) : null;
  const keys = [
    ...new Set([
      ...Object.keys(specialists),
      ...pendingDomains.filter((d) => !specialists[d]),
    ]),
  ];

  if (keys.length === 0) {
    return (
      <div className="specialist-area">
        <div className="spec-panel spec-panel-placeholder">
          <div className="spec-panel-hdr">
            <span className="spec-name">Clinical specialists</span>
            <span className="spec-status">waiting to start…</span>
          </div>
          <div className="spec-body">
            <PanelSkeleton />
          </div>
        </div>
      </div>
    );
  }

  const phase3Done = phase3Status === "done";

  return (
    <div className="specialist-area">
      {keys.map((key) => {
        const spec = specialists[key] ?? { key, paperCount: null, lastTool: null, text: "" };
        const isActive = activeSpecKey === key;
        const streaming = !phase3Done && spec.text.length > 0;
        const waiting = !spec.text && (phase3Status === "active" || isActive);
        return (
          <div key={key} className={`spec-panel${isActive ? " spec-panel-active" : ""}`}>
            <div className="spec-panel-hdr">
              <span className="spec-name">{humanizeSpecialty(key)}</span>
              <span className="spec-status">
                {isActive && !spec.text
                  ? "starting…"
                  : waiting
                    ? "searching…"
                    : spec.paperCount != null
                      ? `${spec.paperCount} papers`
                      : phase3Status === "active"
                        ? "searching…"
                        : ""}
                {spec.lastTool ? (
                  <span className="spec-tool"> · {spec.lastTool.replace(/_/g, " ")}</span>
                ) : null}
              </span>
            </div>
            <div className="spec-body">
              {spec.text ? (
                <p className={`spec-text${streaming ? " streaming" : ""}`}>{spec.text}</p>
              ) : (
                <PanelSkeleton />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
